import React, { useEffect, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { billingService } from '../services/billingService';
import { metricsService } from '../services/metricsService';

/**
 * «Выдать демо клиенту» — админ открывает клиенту месяц «Малого» по данным менеджера офиса.
 *
 * Решение владельца 05.10.2026: при покупке любого продукта компании клиент
 * получает демо-доступ к Ньютону; выдаёт пока только админ. Тариф выдаётся сразу
 * (bit-billing `POST /demo-requests`), одно демо на ИНН. Офис подставляется из
 * адресной книги по почте менеджера: ему засчитаются оплаты клиента (50/25).
 */
export default function DemoAccessDialog({ open, onClose }) {
  const [form, setForm] = useState({ company_name: '', inn: '', client_email: '', manager_email: '', office: '', comment: '' });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  // Офис менеджера — из адресной книги по его почте, когда она введена целиком.
  // Вписанное руками не перетираем.
  const manager = form.manager_email.trim();
  useEffect(() => {
    if (!open || !/\S+@\S+\.\S+/.test(manager)) return undefined;
    let alive = true;
    metricsService.resolveOffices([manager])
      .then((offices) => {
        const office = offices[manager.toLowerCase()];
        if (alive && office) setForm((f) => (f.office ? f : { ...f, office }));
      })
      .catch(() => { /* не нашли офис — впишут руками */ });
    return () => { alive = false; };
  }, [open, manager]);

  if (!open) return null;

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const filled = form.company_name.trim() && /^\d{10}(\d{2})?$/.test(form.inn.trim())
    && /\S+@\S+\.\S+/.test(form.client_email.trim()) && /\S+@\S+\.\S+/.test(manager) && form.office.trim();

  const close = () => {
    setDone(null);
    setError(null);
    setForm({ company_name: '', inn: '', client_email: '', manager_email: '', office: '', comment: '' });
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError(null);
    try {
      const out = await billingService.createDemo({
        company_name: form.company_name.trim(),
        inn: form.inn.trim(),
        client_email: form.client_email.trim(),
        office: form.office.trim(),
        manager_email: manager,
        comment: form.comment.trim() || null,
      });
      setDone({ ...out, office: form.office.trim(), company: form.company_name.trim() });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Не удалось выдать демо — попробуйте позже');
    } finally {
      setSending(false);
    }
  };

  const input = 'w-full rounded-control bg-paper-bg px-3 py-[10px] text-[14px] text-paper-ink outline-none '
    + 'focus:ring-1 focus:ring-paper-accent';
  const label = 'mb-1 block text-[12px] text-paper-ink-2';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,.4)' }}
         role="dialog" aria-modal="true" aria-label="Выдать демо клиенту">
      <div className="flex max-h-[calc(100dvh-2rem)] w-full max-w-[560px] flex-col overflow-y-auto rounded-[18px] bg-paper-card
                      p-5 shadow-[0_24px_64px_rgba(0,0,0,.18)] md:p-7">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-display text-[22px] font-extrabold leading-tight tracking-[-0.03em] text-paper-ink">
              Демо-доступ клиенту
            </h2>
            <p className="mt-2 text-[13px] leading-relaxed text-paper-ink-2">
              Клиент, купивший любой продукт компании, получает месяц Ньютона на тарифе «Малый» бесплатно.
              Оплата после демо засчитается офису менеджера.
            </p>
          </div>
          <button type="button" onClick={close} aria-label="Закрыть" className="-mr-1 -mt-1 shrink-0 p-2 text-paper-ink-3">
            <X className="h-4 w-4" />
          </button>
        </div>

        {done ? (
          <div className="mt-5 rounded-[16px] bg-paper-panel p-5">
            <p className="text-[15px] font-semibold text-paper-ink">Демо выдано</p>
            <p className="mt-2 text-[13px] leading-relaxed text-paper-ink-2">
              «{done.company}»: тариф «Малый» до{' '}
              {new Date(done.paid_until).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}.
              На {done.client_email} ушло письмо, как войти. Оплата после демо засчитается офису «{done.office}».
            </p>
            <button type="button" onClick={close}
                    className="mt-4 rounded-control bg-paper-ink px-4 py-[9px] text-[13px] font-semibold text-white">
              Готово
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-5 flex flex-col gap-3">
            <div>
              <label className={label} htmlFor="demo-company">Компания клиента</label>
              <input id="demo-company" className={input} value={form.company_name} onChange={set('company_name')}
                     placeholder="ООО Ромашка" maxLength={512} />
            </div>
            <div>
              <label className={label} htmlFor="demo-inn">ИНН</label>
              <input id="demo-inn" className={input} value={form.inn} onChange={set('inn')}
                     inputMode="numeric" placeholder="10 или 12 цифр" maxLength={12} />
            </div>
            <div>
              <label className={label} htmlFor="demo-email">Почта администратора клиента</label>
              <input id="demo-email" className={input} value={form.client_email} onChange={set('client_email')}
                     type="email" placeholder="director@romashka.ru" />
              <span className="mt-1 block text-[12px] text-paper-ink-3">
                На неё придёт письмо, с ней клиент входит. Корпоративные адреса Первого Бита не подходят.
              </span>
            </div>
            <div>
              <label className={label} htmlFor="demo-manager">Почта менеджера, продавшего клиенту</label>
              <input id="demo-manager" className={input} value={form.manager_email} onChange={set('manager_email')}
                     type="email" placeholder="ivanov@1cbit.ru" />
            </div>
            <div>
              <label className={label} htmlFor="demo-office">Офис менеджера</label>
              <input id="demo-office" className={input} value={form.office} onChange={set('office')} maxLength={200} />
            </div>
            <div>
              <label className={label} htmlFor="demo-comment">Что купил клиент (необязательно)</label>
              <input id="demo-comment" className={input} value={form.comment} onChange={set('comment')} maxLength={2000} />
            </div>
            {error && <div className="text-[13px] text-destructive">{error}</div>}
            <button type="submit" disabled={!filled || sending}
                    className="mt-1 flex items-center justify-center gap-2 rounded-control bg-paper-accent px-4 py-[11px]
                               text-[14px] font-semibold text-white hover:bg-paper-accent-hover disabled:opacity-50">
              {sending && <Loader2 className="h-4 w-4 animate-spin" />}
              Выдать демо на месяц
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
